import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../store/authStore';
import { FiArrowLeft, FiStar, FiChevronDown, FiAward } from 'react-icons/fi';
import { BsTrophy } from 'react-icons/bs';

interface LeaderEntry {
  rank: number;
  username: string;
  role: string;
  currentLevel: number;
  totalStars: number;
  completedLevels: number;
  isYou?: boolean;
}

const LeaderboardPage = () => {
  const { user } = useAuthStore();
  const [topPlayers, setTopPlayers] = useState<LeaderEntry[]>([]);
  const [aroundYou, setAroundYou] = useState<LeaderEntry[]>([]);
  const [userRank, setUserRank] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [topRes, aroundRes] = await Promise.all([
          api.get('/progress/leaderboard', { params: { page: 1, limit: 20 } }),
          api.get('/progress/leaderboard/around'),
        ]);
        setTopPlayers(topRes.data.leaderboard);
        setAroundYou(aroundRes.data.leaderboard);
        setUserRank(aroundRes.data.userRank);
      } catch (e) {
        console.error('Failed to load leaderboard:', e);
      }
      setIsLoading(false);
    };
    load();
  }, []);

  const rankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#fff', fontSize: 20 }}>
        <div className="spinner"></div> Loading...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', paddingBottom: 40 }}>
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        background: 'rgba(18,18,48,0.9)', backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #2a2a5a', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{
          background: 'rgba(18,18,48,0.7)', border: '1px solid #2a2a5a', borderRadius: 12,
          width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#aaaacc', cursor: 'pointer', textDecoration: 'none',
        }}>
          <FiArrowLeft size={22} />
        </Link>
        <div>
          <h1 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
            <BsTrophy style={{ color: '#ffaa00', marginRight: 8 }} />
            Таблица лидеров
          </h1>
          <span style={{ color: '#8888bb', fontSize: '0.75rem' }}>
            {user?.username} — Место #{userRank} · {user?.totalStars || 0} звёзд
          </span>
        </div>
      </header>

      {/* Around you section */}
      {aroundYou.length > 0 && (
        <section style={{ maxWidth: 500, margin: '16px auto', padding: '0 12px' }}>
          <h2 style={{ color: '#fff', fontSize: '1rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAward color="#00d4ff" /> Рядом с вами
          </h2>
          <div style={{
            background: 'rgba(18,18,48,0.5)', borderRadius: 12, border: '1px solid #2a2a5a',
            overflow: 'hidden',
          }}>
            {aroundYou.map((entry, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                background: entry.isYou ? 'rgba(55,66,250,0.15)' : 'transparent',
                borderTop: i > 0 ? '1px solid #1a1a3a' : 'none',
                borderLeft: entry.isYou ? '3px solid #3742fa' : 'none',
              }}>
                <span style={{
                  fontSize: '1.1rem', fontWeight: 700, width: 40, textAlign: 'center',
                  color: entry.rank <= 3 ? '#ffaa00' : '#8888bb',
                }}>
                  {rankIcon(entry.rank)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: entry.isYou ? '#3742fa' : '#fff', fontWeight: entry.isYou ? 700 : 500,
                    fontSize: '0.9rem',
                  }}>
                    {entry.username} {entry.isYou && <span style={{ color: '#666', fontSize: '0.75rem' }}>(вы)</span>}
                  </div>
                  <div style={{ color: '#666699', fontSize: '0.7rem' }}>
                    Уровень {entry.currentLevel} · {entry.completedLevels} уровней
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiStar size={14} color="#ffaa00" />
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{entry.totalStars}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top players */}
      {topPlayers.length > 0 && (
        <section style={{ maxWidth: 500, margin: '24px auto', padding: '0 12px' }}>
          <h2 style={{ color: '#fff', fontSize: '1rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BsTrophy color="#ffaa00" /> Лучшие игроки
          </h2>
          <div style={{
            background: 'rgba(18,18,48,0.5)', borderRadius: 12, border: '1px solid #2a2a5a',
            overflow: 'hidden',
          }}>
            {topPlayers.map((entry, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                background: entry.isYou ? 'rgba(55,66,250,0.15)' : 'transparent',
                borderTop: i > 0 ? '1px solid #1a1a3a' : 'none',
                borderLeft: entry.isYou ? '3px solid #3742fa' : 'none',
              }}>
                <span style={{
                  fontSize: '1.1rem', fontWeight: 700, width: 40, textAlign: 'center',
                  color: entry.rank <= 3 ? '#ffaa00' : '#8888bb',
                }}>
                  {rankIcon(entry.rank)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>
                    {entry.username}
                    {entry.role === 'ADMIN' && <span style={{ color: '#ff4757', fontSize: '0.65rem', marginLeft: 6 }}>ADMIN</span>}
                  </div>
                  <div style={{ color: '#666699', fontSize: '0.7rem' }}>
                    Уровень {entry.currentLevel} · {entry.completedLevels} уровней
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiStar size={14} color="#ffaa00" />
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{entry.totalStars}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default LeaderboardPage;
